import React, { useState } from "react";
import PropTypes from "prop-types";
import { FaQuestionCircle, FaCheckCircle, FaTimesCircle, FaEye, FaEyeSlash, FaLightbulb } from "react-icons/fa";
import { motion, AnimatePresence } from 'framer-motion';

const QNA = ({ questionsAndAnswers, isDark = false }) => {
  const [showSolutions, setShowSolutions] = useState(false);
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());

  const handleShowSolutions = () => {
    setShowSolutions(!showSolutions);
  };

  const toggleQuestion = (index) => {
    const newExpanded = new Set(expandedQuestions);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedQuestions(newExpanded);
  };

  return (
    <div className="p-4 sm:p-6 md:p-8">
      {/* Header with toggle */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
        <div>
          <h2 className={`text-xl sm:text-2xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-800'
            }`}>
            Review Your Answers
          </h2>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Click on any question to see details
          </p>
        </div>
        <motion.button
          onClick={handleShowSolutions}
          className={`flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-semibold text-sm sm:text-base shadow-lg transition-all duration-300 ${showSolutions
              ? "bg-gradient-to-r from-red-500 to-rose-600 text-white"
              : "bg-gradient-to-r from-green-500 to-emerald-600 text-white"
            }`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {showSolutions ? (
            <>
              <FaEyeSlash className="text-sm sm:text-base" />
              <span>Hide Solutions</span>
            </>
          ) : (
            <>
              <FaEye className="text-sm sm:text-base" />
              <span>Show Solutions</span>
            </>
          )}
        </motion.button>
      </div>

      {/* Questions List */}
      <div className="space-y-4">
        {questionsAndAnswers.map((item, i) => {
          const isExpanded = expandedQuestions.has(i);
          const isCorrect = item.point > 0;
          const isAnswered = item.user_answer !== null;

          return (
            <motion.div
              key={i}
              className={`rounded-xl shadow-lg border-2 transition-all duration-300 overflow-hidden ${isDark
                  ? isCorrect
                    ? "bg-dark-tertiary/50 border-green-500/30 hover:border-green-500/50"
                    : "bg-dark-tertiary/50 border-red-500/30 hover:border-red-500/50"
                  : isCorrect
                    ? "bg-white border-green-200 hover:border-green-300"
                    : "bg-white border-red-200 hover:border-red-300"
                } ${isExpanded ? "shadow-xl" : "hover:shadow-xl"}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              {/* Question Header - Always Visible */}
              <button
                onClick={() => toggleQuestion(i)}
                className={`w-full p-4 sm:p-5 text-left focus:outline-none focus:ring-2 focus:ring-purple-500 rounded-t-xl ${isDark ? 'hover:bg-white/5' : ''
                  }`}
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  {/* Question Number Badge */}
                  <div
                    className={`flex-shrink-0 flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full font-bold text-sm sm:text-base text-white ${isCorrect ? "bg-green-500" : "bg-red-500"
                      }`}
                  >
                    {i + 1}
                  </div>

                  {/* Question Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <FaQuestionCircle
                        className={`text-lg sm:text-xl flex-shrink-0 ${isCorrect ? "text-green-500" : "text-red-500"
                          }`}
                      />
                      <h3 className={`text-sm sm:text-base font-semibold line-clamp-2 ${isDark ? 'text-white' : 'text-gray-800'
                        }`}>
                        {item.question}
                      </h3>
                    </div>

                    {/* Quick Status */}
                    <div className="flex items-center gap-3 flex-wrap">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${isCorrect
                            ? isDark ? "bg-green-500/20 text-green-400" : "bg-green-100 text-green-700"
                            : isDark ? "bg-red-500/20 text-red-400" : "bg-red-100 text-red-700"
                          }`}
                      >
                        {isCorrect ? (
                          <>
                            <FaCheckCircle className="text-xs" />
                            <span>Correct</span>
                          </>
                        ) : (
                          <>
                            <FaTimesCircle className="text-xs" />
                            <span>{isAnswered ? "Incorrect" : "Not Answered"}</span>
                          </>
                        )}
                      </span>
                      {isExpanded && (
                        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                          Click to {isExpanded ? "collapse" : "expand"}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Expand/Collapse Icon */}
                  <div className="flex-shrink-0">
                    <motion.div
                      animate={{ rotate: isExpanded ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <svg
                        className={`w-5 h-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </motion.div>
                  </div>
                </div>
              </button>

              {/* Expanded Content */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    className={`px-4 sm:px-5 pb-4 sm:pb-5 space-y-3 border-t ${isDark ? 'border-white/10' : 'border-gray-100'
                      }`}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {/* Your Answer */}
                    <div className="pt-3">
                      <div className="flex items-start gap-2 mb-2">
                        <span className={`text-xs sm:text-sm font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                          Your Answer:
                        </span>
                      </div>
                      {isAnswered ? (
                        <div
                          className={`p-3 rounded-lg border-2 ${isCorrect
                              ? isDark ? "bg-green-500/10 border-green-500/30 text-green-400" : "bg-green-50 border-green-200 text-green-800"
                              : isDark ? "bg-red-500/10 border-red-500/30 text-red-400" : "bg-red-50 border-red-200 text-red-800"
                            }`}
                        >
                          <p className="text-sm sm:text-base font-medium">
                            {item.user_answer}
                          </p>
                        </div>
                      ) : (
                        <div className={`p-3 rounded-lg border-2 ${isDark ? 'bg-gray-500/10 border-gray-500/30 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-600'
                          }`}>
                          <p className="text-sm sm:text-base font-medium italic">
                            Not answered
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Correct Answer (if wrong or not answered) */}
                    {(!isCorrect || !isAnswered) && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <FaCheckCircle className="text-green-500 text-sm" />
                          <span className={`text-xs sm:text-sm font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                            Correct Answer:
                          </span>
                        </div>
                        <div className={`p-3 rounded-lg border-2 ${isDark ? 'bg-green-500/10 border-green-500/30 text-green-400' : 'bg-green-50 border-green-200 text-green-800'
                          }`}>
                          <p className="text-sm sm:text-base font-medium">
                            {item.correct_answer}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Solution (if available and toggled) */}
                    {showSolutions && item.solution && (
                      <motion.div
                        className="pt-2"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <FaLightbulb className="text-yellow-500 text-sm" />
                          <span className={`text-xs sm:text-sm font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                            Explanation:
                          </span>
                        </div>
                        <div className={`p-3 sm:p-4 rounded-lg border-2 ${isDark
                            ? 'bg-purple-500/10 border-purple-500/30'
                            : 'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200'
                          }`}>
                          <p className={`text-sm sm:text-base leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                            {item.solution}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>

      {/* Summary Footer */}
      <div className={`mt-6 p-4 sm:p-5 rounded-xl border ${isDark
          ? 'bg-purple-500/10 border-purple-500/20'
          : 'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200'
        }`}>
        <p className={`text-center text-sm sm:text-base ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
          <span className={`font-semibold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
            {questionsAndAnswers.filter((q) => q.point > 0).length}
          </span>{" "}
          out of{" "}
          <span className={`font-semibold ${isDark ? 'text-pink-400' : 'text-pink-600'}`}>
            {questionsAndAnswers.length}
          </span>{" "}
          questions answered correctly
        </p>
      </div>
    </div>
  );
};

QNA.propTypes = {
  questionsAndAnswers: PropTypes.array.isRequired,
  isDark: PropTypes.bool,
};

export default QNA;
